import React, { useState, useCallback } from 'react';
import { Card, BlockStack, Text, InlineStack, Select } from '@shopify/polaris';
import { CHART_DEFAULTS, CHART_COLORS } from '../theme';

/**
 * T081: TrendChart component.
 * Line chart with time-window selector and adaptive x-axis label thinning.
 * Uses inline SVG for Polaris-compatible lightweight charting.
 * Colours sourced from theme tokens.
 *
 * @see documents/ui-enhancement-spec.md §4.1 (Chart Colours)
 */

interface TrendPoint {
  date: string;
  score: number;
}

interface TrendChartProps {
  /** All available data points, oldest-first, from the API. */
  data: TrendPoint[];
  /** The store's tier history window in days — controls which window tabs are shown. */
  maxDays?: number;
}

const WINDOW_OPTIONS: { label: string; days: number }[] = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '1Y', days: 365 },
];

/** Maximum x-axis labels to display regardless of data size. */
const MAX_X_LABELS = 10;

const { width: CHART_WIDTH, height: CHART_HEIGHT, padding: PADDING } = CHART_DEFAULTS;

export function TrendChart({
  data,
  maxDays = 7,
}: Readonly<TrendChartProps>): React.ReactElement {
  const availableWindows = WINDOW_OPTIONS.filter((w) => w.days <= maxDays);

  // Default to the largest available window, but cap at 30D so the initial
  // view is not overwhelming when a user first opens the dashboard.
  const defaultWindow = availableWindows.length > 1
    ? Math.min(30, maxDays)
    : maxDays;
  const [selectedDays, setSelectedDays] = useState(defaultWindow);
  const handleWindowChange = useCallback((v: string) => setSelectedDays(Number(v)), []);

  // Slice to the selected window (data is oldest-first, so take from the end)
  const windowedData = data.slice(-selectedDays);

  if (windowedData.length === 0) {
    return (
      <Card>
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">Score Trend</Text>
          <Text as="p" variant="bodySm" tone="subdued">
            No trend data available yet. Scores will appear after your first scan.
          </Text>
        </BlockStack>
      </Card>
    );
  }

  const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;

  const scores = windowedData.map((d) => d.score);
  const minScore = Math.max(0, Math.min(...scores) - 5);
  const maxScore = Math.min(100, Math.max(...scores) + 5);
  const scoreRange = maxScore - minScore || 1;

  const xScale = (index: number) =>
    PADDING.left + (index / Math.max(windowedData.length - 1, 1)) * innerWidth;

  const yScale = (score: number) =>
    PADDING.top + innerHeight - ((score - minScore) / scoreRange) * innerHeight;

  // Build SVG path
  const pathD = windowedData
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.score)}`)
    .join(' ');

  // Area fill path
  const areaD = `${pathD} L ${xScale(windowedData.length - 1)} ${yScale(minScore)} L ${xScale(0)} ${yScale(minScore)} Z`;

  // Y-axis grid lines (5 lines)
  const yGridLines = Array.from({ length: 5 }, (_, i) => {
    const score = minScore + (scoreRange * (i + 1)) / 5;
    return { y: yScale(score), label: Math.round(score).toString() };
  });

  // Adaptive label stride: show at most MAX_X_LABELS labels
  const labelStride = Math.ceil(windowedData.length / MAX_X_LABELS);

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">Score Trend</Text>
          {availableWindows.length > 1 && (
            <Select
              label="Time window"
              labelHidden
              options={availableWindows.map((w) => ({ label: w.label, value: String(w.days) }))}
              value={String(selectedDays)}
              onChange={handleWindowChange}
            />
          )}
        </InlineStack>
        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          width="100%"
          height={CHART_HEIGHT}
          role="img"
          aria-label={`Score trend chart showing ${windowedData.length} data points over ${selectedDays} days`}
        >
          {/* Y-axis grid lines */}
          {yGridLines.map((line, i) => (
            <g key={`grid-${i}`}>
              <line
                x1={PADDING.left}
                y1={line.y}
                x2={CHART_WIDTH - PADDING.right}
                y2={line.y}
                stroke={`var(--z-chart-grid, ${CHART_COLORS.gridLight})`}
                strokeWidth="1"
              />
              <text
                x={PADDING.left - 8}
                y={line.y + 4}
                textAnchor="end"
                fontSize="11"
                fill={`var(--z-chart-label, ${CHART_COLORS.labelLight})`}
              >
                {line.label}
              </text>
            </g>
          ))}

          {/* Area fill (gradient effect) */}
          <path d={areaD} fill={`var(--z-chart-line, ${CHART_COLORS.lineLight})`} opacity="0.1" />

          {/* Line */}
          <path
            d={pathD}
            fill="none"
            stroke={`var(--z-chart-line, ${CHART_COLORS.lineLight})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points — rendered for all points, smaller for large windows */}
          {windowedData.map((d, i) => (
            <circle
              key={`point-${i}`}
              cx={xScale(i)}
              cy={yScale(d.score)}
              r={windowedData.length > 30 ? 2 : 4}
              fill={`var(--z-chart-line, ${CHART_COLORS.lineLight})`}
              stroke="var(--z-surface, white)"
              strokeWidth={windowedData.length > 30 ? 1 : 2}
            />
          ))}

          {/* X-axis labels — thinned to MAX_X_LABELS regardless of window size */}
          {windowedData.map((d, i) => {
            const isFirst = i === 0;
            const isLast = i === windowedData.length - 1;
            if (!isFirst && !isLast && i % labelStride !== 0) return null;
            const label = d.date.slice(5); // "MM-DD"
            return (
              <text
                key={`label-${i}`}
                x={xScale(i)}
                y={CHART_HEIGHT - 8}
                textAnchor="middle"
                fontSize="10"
                fill={`var(--z-chart-label, ${CHART_COLORS.labelLight})`}
              >
                {label}
              </text>
            );
          })}
        </svg>
      </BlockStack>
    </Card>
  );
}
